<template>
    <div class="search-page">
        <div v-if="isLoaded" class="page-content">
            <div class="cover"></div>
            <div class="container">
                <div class="content">
                    <h1>Vizualizare comandă</h1>
                    <p>Actualizată ultima oară la {{(new Date(order.order.updated_at)).toLocaleString()}}</p>

                    <div class="page" v-if="order.products.length === 0">
                        <h1>Niciun produs în comandă</h1>
                        <p>
                            Nu aveți niciun produs adăugat în această comandă. Puteți începe prin a căuta produsul
                            dorit, utilizând căsuța de căutare.
                        </p>
                    </div>

                    <div v-else>
                        <div class="page">
                            <h1>Comanda #{{orderID}}</h1>

                            <h2>Acțiuni</h2>

                            <div class="row">
                                <b-col sm="12">
                                    <router-link :to="`/order/${orderID}/edit`" v-slot="{ href, navigate }">
                                        <a :href="href" @click="navigate">
                                            <b-button variant="secondary">
                                                <b-icon icon="pencil-square" />
                                                Editează
                                            </b-button>
                                        </a>
                                    </router-link>
                                    <b-button class="ml-2" variant="danger" @click="() => deleteOrder()">
                                        <b-icon icon="trash" />
                                        Șterge
                                    </b-button>
                                </b-col>
                            </div>

                            <h2 class="mt-3">Export</h2>
                            <table class="table table-sm table-borderless table-striped">
                                <thead>
                                    <tr>
                                        <th scope="col">Furnizor</th>
                                        <th scope="col" class="nowrap">Număr produse</th>
                                        <th scope="col" class="nowrap">Acțiuni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="supplier in this.order.suppliers" :key="supplier.supplier_id">
                                        <td>{{ supplier.supplier_name }}</td>
                                        <td>{{ getSupplierProducts(supplier.supplier_id).length }}</td>
                                        <td class="nowrap">
                                            <b-button variant="outline-danger" size="sm" @click="() => downloadPdfFile(supplier.supplier_id)">
                                                <b-icon icon="file-text" />
                                                PDF
                                            </b-button>
                                            <b-button class="ml-2" variant="outline-success" size="sm" @click="() => downloadXlsxFile(supplier.supplier_id)">
                                                <b-icon icon="file-spreadsheet" />
                                                Excel (XLSX)
                                            </b-button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-if="hasIssues" class="red page">
                            <h1>Probleme de integritate</h1>

                            <p>
                                Au fost găsite probleme de integritate la această comandă.
                                Acest lucru înseamnă că informațiile de mai jos nu mai sunt de actualitate.
                            </p>

                            <p>
                                Vă rugăm să luați la cunoștință erorile de mai jos, și în cazul în care acestea
                                pun probleme, să le remediați din secțiunea de editare a comenzii.
                            </p>

                            <template v-if="this.issues.changed_order_items.length > 0">
                                <hr />

                                <h2>Informații de stoc actualizate</h2>
                                <p>
                                    Au fost detectate schimbări ale informațiilor de stoc utilizate în cadrul acestei comenzi:
                                </p>

                                <table class="table table-sm table-borderless table-striped">
                                    <thead>
                                        <tr>
                                            <th scope="col">Denumire produs</th>
                                            <th scope="col">Furnizor</th>
                                            <th scope="col" class="nowrap">Preț vechi</th>
                                            <th scope="col" class="nowrap">Preț nou</th>
                                            <th scope="col" class="nowrap">Stoc vechi</th>
                                            <th scope="col" class="nowrap">Stoc nou</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(info, index) in this.issues.changed_order_items" :key="index">
                                            <td>{{info.product_name}}</td>
                                            <td>{{info.supplier_name}}</td>

                                            <template v-if="(info.old_product_stock.price != info.new_product_stock.price) || (info.old_product_stock.currency != info.new_product_stock.currency)">
                                                <td class="nowrap">{{parseFloat(info.old_product_stock.price).toFixed(2)}} {{info.old_product_stock.currency}}</td>
                                                <td class="nowrap">{{parseFloat(info.new_product_stock.price).toFixed(2)}} {{info.new_product_stock.currency}}</td>
                                            </template>
                                            <template v-else-if="info.old_product_stock.ron_price != info.new_product_stock.ron_price">
                                                <td class="nowrap">{{parseFloat(info.old_product_stock.ron_price).toFixed(2)}} RON</td>
                                                <td class="nowrap">{{parseFloat(info.new_product_stock.ron_price).toFixed(2)}} RON</td>
                                            </template>
                                            <template v-else>
                                                <td class="nowrap"></td>
                                                <td class="nowrap"></td>
                                            </template>

                                            <template v-if="info.old_product_stock.quantity != info.new_product_stock.quantity">
                                                <td class="nowrap">{{parseFloat(info.old_product_stock.quantity).toFixed(0)}}</td>
                                                <td class="nowrap">{{parseFloat(info.new_product_stock.quantity).toFixed(0)}}</td>
                                            </template>
                                            <template v-else>
                                                <td class="nowrap"></td>
                                                <td class="nowrap"></td>
                                            </template>
                                        </tr>
                                    </tbody>
                                </table>
                            </template>

                            <template v-if="this.issues.unavailable_order_items.length > 0">
                                <hr />

                                <h2>Informații de stoc radiate</h2>
                                <p>
                                    Au fost detectate ștergeri ale informațiilor de stoc utilizate în cadrul acestei comenzi.
                                    Nu s-au putut găsi automat versiuni mai noi ale acestor informații.
                                </p>
                                <p>Produse la care s-au constatat astfel de erori:</p>
                                <ul>
                                    <li v-for="(info, index) in this.issues.unavailable_order_items" :key="index">
                                        <strong>{{info.product_name}}</strong>
                                    </li>
                                </ul>
                            </template>
                        </div>

                        <div class="page" v-for="supplier in this.order.suppliers" :key="supplier.supplier_id">
                            <h1>{{ supplier.supplier_name }}</h1>

                            <table class="table table-sm table-borderless table-striped">
                                <thead>
                                    <tr>
                                        <th scope="col">Denumire</th>
                                        <th scope="col">Producător</th>
                                        <th scope="col">Preț / buc.</th>
                                        <th scope="col">Cantitate</th>
                                        <th scope="col" class="nowrap">Preț final</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="product in getSupplierProducts(supplier.supplier_id)" :key="product.item_id">
                                        <td>
                                            {{product.name}}
                                            <br/>
                                            <div class="product-codes">
                                                <span v-if="product.codes.product_info.ean">EAN: {{product.codes.product_info.ean}}</span>
                                                <span v-if="product.codes.product_info.upc">UPC: {{product.codes.product_info.upc}}</span>
                                                <span v-if="product.codes.product_info.oem_codes">
                                                    <span>OEM: </span>
                                                    <span v-for="code in product.codes.product_info.oem_codes" :key="code.id">
                                                        {{code.oem_code}}
                                                    </span>
                                                </span>
                                                <span v-for="code in product.codes.extra_ids" :key="code.value">{{code.id_name}}: {{code.value}}</span>
                                            </div>
                                        </td>
                                        <td>{{product.manufacturer}}</td>

                                        <td class="nowrap">
                                            <span>{{parseFloat(product.supplier_price).toFixed(2)}} {{product.supplier_currency}}</span><br/>
                                            <span class="small-text">{{parseFloat(product.base_ron_price).toFixed(2)}} RON</span>
                                        </td>

                                        <td class="nowrap">{{product.quantity}}</td>

                                        <td class="nowrap">
                                            <span>{{(product.supplier_price * product.quantity).toFixed(2)}} {{product.supplier_currency}}</span><br>
                                        </td>
                                    </tr>

                                </tbody>
                            </table>

                            <div class="total" v-for="(total, currency) in supplier.supplier_totals" :key="currency">
                                <span class="mr-3">Total <strong>{{ currency }}</strong>:</span>
                                <span class="amount">{{parseFloat(total).toFixed(2)}} {{ currency }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from "@/utils/axios-instance";

    export default {
        name: 'Order',
        components: {},
        data() {
            return {
                isLoaded: false,
                order: null,
                issues: null,
            }
        },
        computed: {
            orderID() {
                return this.$route.params.id
            },
            hasIssues() {
                if(!this.issues) return false;

                if(this.issues.changed_order_items.length > 0) return true;
                if(this.issues.unavailable_order_items.length > 0) return true;

                return false;
            }
        },
        created: async function () {
            let loader = this.$loading.show();

            await this.loadOrder();
            await this.loadIssues();

            this.isLoaded = true;

            loader.hide();
        },
        watch: {
            // call again the method if the route changes
            '$route': 'fetchData'
        },
        methods: {
            async loadOrder() {
                let response = await axios.get(`order/${this.orderID}`);
                this.order = response.data;
            },
            async loadIssues() {
                let response = await axios.get(`order/${this.orderID}/integrity-issues`);
                this.issues = response.data;
            },
            async deleteOrder() {
                let loader = this.$loading.show();

                try {
                    await axios.delete(`order/${this.orderID}`);
                    await this.$router.push(`/saved-orders`);
                } catch (e) {
                    console.log(e);
                }

                loader.hide();
            },

            /**
             * Filter a supplier's items from the whole list.
             */
            getSupplierProducts(supplierId) {
                let filteredProducts = this.order.products.filter(product => {
                    return product.supplier_id === supplierId;
                });

                filteredProducts.sort((productA, productB) => {
                    let orderA = productA.order;
                    let orderB = productB.order;

                    return orderA - orderB;
                });

                return filteredProducts;
            },

            async downloadPdfFile(supplierId) {
                let loader = this.$loading.show();

                let response = await axios({
                    url: `order/${this.orderID}/supplier/${supplierId}/view/pdf`,
                    method: 'GET',
                    responseType: 'blob',
                });


                let fileURL = window.URL.createObjectURL(new Blob([response.data]));
                let fileLink = document.createElement('a');

                fileLink.href = fileURL;
                fileLink.setAttribute('download', 'Order.pdf');
                document.body.appendChild(fileLink);

                fileLink.click();

                loader.hide();
            },
            async downloadXlsxFile(supplierId) {
                let loader = this.$loading.show();

                try {
                    let response = await axios({
                        url: `order/${this.orderID}/supplier/${supplierId}/view/xlsx`,
                        method: 'GET',
                        responseType: 'blob',
                    });


                    let fileURL = window.URL.createObjectURL(new Blob([response.data]));
                    let fileLink = document.createElement('a');

                    fileLink.href = fileURL;
                    fileLink.setAttribute('download', 'Order.xlsx');
                    document.body.appendChild(fileLink);

                    fileLink.click();
                } catch (e) {
                    console.log(e);
                }

                loader.hide();
            },
        }
    }
</script>

<style scoped lang="scss">
.search-page {
  flex-grow: 1;
  display: flex;
}

.page-content {
  flex-grow: 1;
  position: relative;
}

.cover {
  width: 100%;
  height: 264px;
  position: relative;
  background: #024A8C;
}

.container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.content {
  margin-top: 60px;
  color: white;
  width: 100%;
}

.page {
  background: white;
  border-radius: 5px;
  color: black;
  padding: 25px 20px;

  margin-top: 50px;
  margin-bottom: 20px;

  -webkit-box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);
  -moz-box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);
  box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);

  p {
    margin: 0;
  }

  h1 {
    color: #024A8C;
    font-weight: 600;
  }

  h2, h3, h4, h5, h6 {
    color: #6F6F6F;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  table th {
    color: #024A8C;
    font-weight: 600;
  }
}

.red.page {
  background: darkred;
  color: #e8e8e8;

  h1, h2, h3, h4, h5, h6 {
    color: #FFFFFF;
  }

  table th {
    color: #dadada;
  }

  table td {
    color: #FFFFFF;
  }
}


h1 {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}

h3 {
  font-size: 1rem;
}

table td {
  white-space: normal;
  vertical-align: middle;
}

table td.nowrap, table th.nowrap {
  width: 1%;
  white-space: nowrap;
}

.small-text {
  color: #6F6F6F;
  font-size: 0.7rem;
  word-wrap: break-word;
}

.product-codes {
  color: #6F6F6F;
  font-size: 0.7rem;
  word-wrap: break-word;
}

.product-codes > span::after {
  content: '; ';
}

.total {
  color: #024A8C;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.total .amount {
  font-weight: 800;
  font-size: 1.3rem;
}

.label {
  color: #024A8C;
  font-weight: 600;
}
</style>
